/**
 * S6 run catalog table. Filtering remains in the page container.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import { RunStatusBadge } from './RunStatusBadge';
import type { AiStudioRun } from '@/types/aiStudioAgent';

interface RunTableProps {
  runs: AiStudioRun[];
  onSelect: (runId: string) => void;
}

const HEADER_CLASS =
  'px-3 py-2 text-left text-base font-medium text-[var(--color-surface-text-secondary)]';
const CELL_CLASS = 'px-3 py-2 text-base text-[var(--color-surface-text)]';

export const RunTable: React.FC<RunTableProps> = ({ runs, onSelect }) => {
  const { t } = useLanguage();

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className={HEADER_CLASS}>{t('aiStudio.runs.columnName')}</th>
            <th className={HEADER_CLASS}>{t('aiStudio.runs.columnBinding')}</th>
            <th className={HEADER_CLASS}>{t('aiStudio.runs.columnAgent')}</th>
            <th className={HEADER_CLASS}>{t('aiStudio.runs.columnStatus')}</th>
            <th className={HEADER_CLASS}>{t('aiStudio.runs.columnOwner')}</th>
            <th className={HEADER_CLASS}>{t('aiStudio.runs.columnUpdated')}</th>
          </tr>
        </thead>
        <tbody>
          {runs.map((run) => (
            <tr
              key={run.id}
              tabIndex={0}
              role="link"
              onClick={() => onSelect(run.id)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelect(run.id);
                }
              }}
              className="cursor-pointer border-b border-[var(--color-border)] hover:bg-[var(--color-surface-hover)] focus:outline-none focus:ring-2 focus:ring-[var(--color-info)]"
            >
              <td className={`${CELL_CLASS} font-medium`}>{run.runName}</td>
              <td className={CELL_CLASS}>
                {run.bindingName ?? t('aiStudio.common.notSet')}
              </td>
              <td className={CELL_CLASS}>
                {run.agentName
                  ? `${run.agentName}${run.agentCode ? ` (${run.agentCode})` : ''}`
                  : t('aiStudio.common.notSet')}
              </td>
              <td className={CELL_CLASS}>
                <RunStatusBadge status={run.status} />
              </td>
              <td className={CELL_CLASS}>
                {run.ownerName ?? run.ownerEmail ?? t('aiStudio.common.notSet')}
              </td>
              <td className={CELL_CLASS}>{new Date(run.updatedAt).toLocaleDateString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
